<template>
  <b-form @keydown="form.errors.clear($event.target.id)" @submit.prevent="onSubmit">
    <div class="row">
      <div class="col">
        <b-card>
          <template #header>Unavailabilities for {{ currentUser.name }}</template>

          <tabbed-list
            ref="unavailabilitiesList"
            v-slot="slotProps"
            :alt-select-validator="altSelectItemslotsValidator"
            :config="tabConfigs"
            :height="529"
            :select-validator="selectTimeslotValidator"
            initial-tab="future"
            list-class="mb-3"
            name="unavailabilities"
            @altSelected="altSelectItemslots"
            @selected="selectTimeslot"
            @tabbed="updateTab"
          >
            <div class="w-75">
              <b>
                <template v-if="slotProps.item.start_from_time === '00:00:00'">
                  {{ formatDate(slotProps.item.start_from, "ddd DD MMM YYYY") }}
                </template>
                <template v-else>
                  {{ formatDate(slotProps.item.start_from) }}
                </template>
                -
                <template v-if="slotProps.item.end_by_time === '23:59:59'">
                  {{ formatDate(slotProps.item.end_by, "ddd DD MMM YYYY") }}
                </template>
                <template v-else>
                  {{ formatDate(slotProps.item.end_by) }}
                </template>
              </b>
            </div>

            <div class="text-pre">{{ slotProps.item.reason }}</div>

            <small class="text-muted">
              {{ formatLocalDate(slotProps.item.updated_at) }}
              by
              {{ slotProps.item.creator ? slotProps.item.creator.name : "Anonymous" }}
            </small>

            <font-awesome-icon
              v-if="!isPast(slotProps.item.end_by)"
              :icon="['fal', 'trash']"
              class="trash-entity cursor"
              @click.stop="deleteTimeslot(slotProps.item)"
            />
          </tabbed-list>

          <div v-show="altSelectedTimeslotIds.length === 0">
            <b-alert :show="currentTabKey === 'future'" variant="info" class="mb-2">
              <code>LEFT ALT + Click</code> to select multiple unavailabilities to delete (only
              future unavailabilities can be deleted)
            </b-alert>

            <form-textarea v-model="form.reason" :single-row="false" name="reason" />

            <form-bootstrap-radio
              v-if="!selectedTimeslotId"
              v-model="form.recurring.type"
              :options="recurringTypes"
              :single-row="false"
              name="recurring_type"
              @input="recurringTypeChange()"
            />

            <template v-if="form.recurring.type === 'Weekly'">
              <form-select
                v-model="form.recurring.weekly_interval"
                :options="calendar.weeklyIntervals"
                :single-row="false"
                error-name="recurring.weekly_interval"
                name="weekly_interval"
                @input="form.errors.clear()"
              />
            </template>
            <template v-else-if="form.recurring.type === 'Monthly'">
              <form-select
                v-model="form.recurring.monthly_interval"
                :options="calendar.monthlyIntervals"
                :single-row="false"
                error-name="recurring.monthly_interval"
                name="monthly_interval"
                @input="form.errors.clear()"
              />
            </template>

            <div class="row">
              <div class="col">
                <div class="row">
                  <template v-if="form.recurring.type === 'Not Recurring'">
                    <div class="col">
                      <form-date
                        v-model="form.start_from_date"
                        :single-row="false"
                        name="start_from_date"
                        value-format="yyyy-MM-dd"
                        @input="startDateChanged"
                      />
                    </div>
                  </template>
                  <template v-else-if="form.recurring.type === 'Weekly'">
                    <div class="col">
                      <form-select
                        v-model="form.recurring.weekly_day_start_from"
                        :options="calendar.days"
                        :single-row="false"
                        name="weekly_day_start_from"
                        @input="form.errors.clear()"
                        >Start from
                      </form-select>
                    </div>
                  </template>
                  <template v-else-if="form.recurring.type === 'Monthly'">
                    <div class="col">
                      <form-select
                        v-model="form.recurring.monthly_ordinal_start_from"
                        :options="calendar.weekDayOrdinals"
                        :single-row="false"
                        error-name="recurring.monthly_ordinal_start_from"
                        name="monthly_ordinal_start_from"
                        @input="form.errors.clear()"
                        >Start from
                      </form-select>
                    </div>
                    <div class="col">
                      <form-select
                        v-model="form.recurring.monthly_day_start_from"
                        :options="calendar.days"
                        :single-row="false"
                        name="monthly_day_start_from"
                        @input="form.errors.clear()"
                        >&nbsp;
                      </form-select>
                    </div>
                  </template>
                </div>
                <div class="row">
                  <div class="col">
                    <form-time
                      v-model="form.start_from_time"
                      :single-row="false"
                      name="start_from_time"
                      @input="form.errors.clear()"
                    >
                      <template #note>
                        <small class="text-muted">Optional, default to the start of the day</small>
                      </template>
                    </form-time>
                  </div>
                </div>
              </div>

              <div class="col">
                <div v-show="form.recurring.type === 'Not Recurring'" class="row">
                  <div class="col">
                    <form-date
                      v-model="form.end_by_date"
                      :single-row="false"
                      name="end_by_date"
                      value-format="yyyy-MM-dd"
                      @input="form.errors.clear()"
                    />
                  </div>
                </div>
                <div v-show="form.recurring.type === 'Weekly'" class="row">
                  <div class="col">
                    <form-text
                      v-model.trim="form.recurring.weekly_day_duration_number"
                      :is-number="true"
                      :single-row="false"
                      error-name="recurring.weekly_day_duration_number"
                      max="99"
                      min="1"
                      name="weekly_day_duration_number"
                      @input="form.errors.clear()"
                    >
                      Duration
                    </form-text>
                  </div>
                  <div class="col">
                    <form-select
                      v-model="form.recurring.weekly_day_duration_unit"
                      :options="['Day', 'Week']"
                      :single-row="false"
                      error-name="recurring.weekly_day_duration_unit"
                      name="weekly_day_duration_unit"
                      @input="form.errors.clear()"
                      >&nbsp;
                    </form-select>
                  </div>
                </div>
                <div v-show="form.recurring.type === 'Monthly'" class="row">
                  <div class="col">
                    <form-text
                      v-model.trim="form.recurring.monthly_day_duration_number"
                      :is-number="true"
                      :single-row="false"
                      error-name="recurring.monthly_day_duration_number"
                      max="99"
                      min="1"
                      name="monthly_day_duration_number"
                      @input="form.errors.clear()"
                    >
                      Duration
                    </form-text>
                  </div>
                  <div class="col">
                    <form-select
                      v-model="form.recurring.monthly_day_duration_unit"
                      :options="['Day', 'Week', 'Month']"
                      :single-row="false"
                      error-name="recurring.monthly_day_duration_unit"
                      name="monthly_day_duration_unit"
                      @input="form.errors.clear()"
                      >&nbsp;
                    </form-select>
                  </div>
                </div>

                <div class="row">
                  <div class="col">
                    <form-time
                      v-model="form.end_by_time"
                      :single-row="false"
                      name="end_by_time"
                      @input="form.errors.clear()"
                    >
                      <template #note>
                        <small class="text-muted">Optional, default to the end of the day</small>
                      </template>
                    </form-time>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="form.recurring.type !== 'Not Recurring'" class="padder">
              <div class="row">
                <div class="col">
                  <form-date
                    v-model="form.recurring.create_from"
                    :picker-type="rangePickerType"
                    :single-row="false"
                    error-name="recurring.create_from"
                    name="create_from"
                    @input="form.errors.clear()"
                  >
                    Create from
                  </form-date>
                </div>
                <div class="col">
                  <form-text
                    v-model.trim="form.recurring.occurences"
                    :is-number="true"
                    :single-row="false"
                    error-name="recurring.occurences"
                    max="99"
                    min="2"
                    name="occurences"
                    @input="form.errors.clear()"
                    >End after (times)
                  </form-text>
                </div>
                <div class="col">
                  <form-date
                    v-model="form.recurring.not_later_than"
                    :picker-type="rangePickerType"
                    :single-row="false"
                    error-name="recurring.not_later_than"
                    name="not_later_than"
                    @input="form.errors.clear()"
                  >
                    Not later than
                  </form-date>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <button
                :disabled="form.notReady()"
                class="btn btn-secondary"
                @click.prevent="reset(true)"
              >
                <font-awesome-icon :icon="['fal', 'undo']" />
                <template v-if="!creating"> New Unavailability</template>
                <template v-else> Reset</template>
              </button>
            </div>
            <div class="col text-right">
              <div v-if="altSelectedTimeslotIds.length === 0">
                <button v-if="creating" :disabled="!isValidSubmission()" class="btn btn-primary">
                  <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
                  <font-awesome-icon v-else :icon="['fal', 'plus-circle']" />
                  Add
                </button>

                <button v-else :disabled="form.notReady()" class="btn btn-primary">
                  <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
                  <font-awesome-icon v-else :icon="['fal', 'pencil']" />
                  Save
                </button>
              </div>
              <div v-else>
                <button
                  :disabled="form.notReady()"
                  class="btn btn-danger"
                  @click.prevent="deleteBatch"
                >
                  <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
                  <font-awesome-icon v-else :icon="['fal', 'trash']" />
                  Delete selected
                </button>
              </div>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <availability
      ref="availabilityWidget"
      :filter-by-user-ids="[currentUser.id]"
      :init-date="availabilityDate"
      :simple-mode="true"
    />
  </b-form>
</template>

<script type="text/babel">
import moment from "moment";
import calendar from "../../../mixins/calendar";
import format from "../../../mixins/format";
import EventBus from "../../../imports/event-bus";
import Availability from "../../../components/common/Availability.vue";
import TabbedList from "@/components/common/TabbedList.vue";

const initialState = {
  id: null,
  user_id: null,
  reason: null,
  start_from_date: null,
  start_from_time: null,
  end_by_date: null,
  end_by_time: null,

  recurring: {
    type: "Not Recurring",

    weekly_interval: 1,
    weekly_day_start_from: null,
    weekly_day_duration_number: 1,
    weekly_day_duration_unit: "Day",

    monthly_interval: 1,
    monthly_ordinal_start_from: "First",
    monthly_day_start_from: "Monday",
    monthly_day_duration_number: 1,
    monthly_day_duration_unit: "Day",

    create_from: "",
    occurences: "",
    not_later_than: "",
  },
};

export default {
  components: {
    Availability,
    TabbedList,
  },
  mixins: [calendar, format],

  props: {
    currentUser: {
      required: true,
    },
  },

  data() {
    return {
      currentTabKey: "future",
      recurringTypes: ["Not Recurring", "Daily", "Weekly", "Monthly"],

      creating: true,
      rangePickerType: "date",

      page: null,
      lastPage: null,
      loadingMore: false,

      tabConfigs: {
        past: {
          title: "Past",
          variant: "warning",
          url: `/users/${this.currentUser.id}/unavailabilities?limit=20&type=past`,
          tabListClass: "past-list",
        },
        future: {
          title: "Future",
          variant: "info",
          url: `/users/${this.currentUser.id}/unavailabilities?limit=20&type=future`,
          tabListClass: "",
        },
      },

      availabilityDate: null,
      form: new window.AppForm(window.clone(initialState)),
    };
  },

  computed: {
    selectedTimeslotId: {
      cache: false,
      get() {
        if (this.$refs && this.$refs.unavailabilitiesList) {
          return this.$refs.unavailabilitiesList.selectedItemId;
        }

        return null;
      },
    },

    altSelectedTimeslotIds: {
      cache: false,
      get() {
        if (this.$refs && this.$refs.unavailabilitiesList) {
          return this.$refs.unavailabilitiesList.altselectedItemIds;
        }

        return [];
      },
    },
  },

  created() {
    this.reset(true);
  },

  methods: {
    updateTab(tabKey) {
      this.currentTabKey = tabKey;
    },

    reset(resetBatchToo, resetTabType) {
      this.creating = true;

      if (this.$refs && this.$refs.unavailabilitiesList) {
        this.$refs.unavailabilitiesList.resetSelected();

        if (resetTabType === undefined || resetTabType) {
          this.$refs.unavailabilitiesList.resetTab();
        }

        if (resetBatchToo) {
          this.$refs.unavailabilitiesList.resetAltSelected();
        }
      }

      this.form.reset();
      this.form.user_id = this.currentUser.id;
      this.form.recurring = window.clone(initialState.recurring);
    },

    selectTimeslotValidator(timeslot) {
      return !this.isPast(timeslot.end_by);
    },

    altSelectItemslotsValidator(timeslot) {
      if (!timeslot || !timeslot.id) {
        return false;
      }

      return !this.isPast(timeslot.end_by);
    },

    selectTimeslot(timeslot) {
      this.form.errors.clear();
      this.creating = false;

      this.form.loadData(timeslot);
      this.form.recurring.type = "Not Recurring";
      this.parseFormData();

      this.startDateChanged();
    },

    altSelectItemslots() {
      this.reset(false, false);
    },

    parseFormData() {
      if (this.form.start_from_time && this.form.start_from_time.length === 5) {
        this.form.start_from_time = `${this.form.start_from_time}:00`;
      }

      if (this.form.end_by_time && this.form.end_by_time.length === 5) {
        this.form.end_by_time = `${this.form.end_by_time}:00`;
      }

      if (this.form.start_from_time === "00:00:00") {
        this.form.start_from_time = null;
      }

      if (this.form.end_by_time === "23:59:59") {
        this.form.end_by_time = null;
      }
    },

    onSubmit() {
      this.parseFormData();

      if (this.creating) {
        if (this.form.recurring.type === "Not Recurring") {
          return this.createSingleUnavailability();
        }

        return this.createMultipleUnavailabilities();
      }

      return this.updateUnavailability();
    },

    startDateChanged() {
      this.availabilityDate = this.form.start_from_date;
      this.$refs.availabilityWidget.init();
      this.form.errors.clear();
    },

    createSingleUnavailability() {
      this.form.busy = true;

      this.form.post("/unavailabilities").then(() => {
        this.form.busy = false;
        EventBus.$emit("user-updated");

        this.reset();
        this.$refs.availabilityWidget.refresh();
        this.$refs.unavailabilitiesList.init();
        window.toastr.success("Unavailability added!", "Success");
      });
    },

    createMultipleUnavailabilities() {
      // Determine create from and not later than based on recurring type
      const createFromMoment = moment(this.form.recurring.create_from);
      const notLaterThanMoment = moment(this.form.recurring.not_later_than);
      switch (this.form.recurring.type) {
        case "Monthly":
          this.form.recurring.create_from = createFromMoment.startOf("month").format("YYYY-MM-DD");
          this.form.recurring.not_later_than = notLaterThanMoment
            .endOf("month")
            .format("YYYY-MM-DD");
          break;

        default:
          this.form.recurring.create_from = createFromMoment.format("YYYY-MM-DD");
          this.form.recurring.not_later_than = notLaterThanMoment.format("YYYY-MM-DD");
      }

      this.form.recurring.create_from =
        this.form.recurring.create_from === "Invalid date" ? "" : this.form.recurring.create_from;
      this.form.recurring.not_later_than =
        this.form.recurring.not_later_than === "Invalid date"
          ? ""
          : this.form.recurring.not_later_than;

      this.form.busy = true;
      this.form.post("/unavailabilities").then((response) => {
        this.form.busy = false;
        EventBus.$emit("user-updated");

        this.reset();
        this.$refs.availabilityWidget.refresh();
        this.$refs.unavailabilitiesList.init();
        if (response.status === 200) {
          window.toastr.success("Recurring Unavailabilities added!", "Success");
        }
      });
    },

    updateUnavailability() {
      this.form.put(`/unavailabilities/${this.form.id}`).then(() => {
        EventBus.$emit("user-updated");

        this.reset();
        this.$refs.availabilityWidget.refresh();
        this.$refs.unavailabilitiesList.init();
        window.toastr.success("Unavailability updated!", "Success");
      });
    },

    deleteTimeslot(timeslot) {
      if (this.form.busy) {
        return;
      }

      window
        .sweetAlert({
          title: "Are you sure?",
          text: "Are you sure that you want to delete this entry?",
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            confirm: "Yes!",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.form.busy = true;

            this.$http.delete(`/unavailabilities/${timeslot.id}`).then((response) => {
              if (response.status === 200) {
                this.$refs.unavailabilitiesList.init();

                window.toastr.success("Unavailability deleted!", "Success");
              }

              this.form.busy = false;
              EventBus.$emit("user-updated");
            });
          }
        });
    },

    deleteBatch() {
      if (this.form.busy) {
        return;
      }

      const count = this.altSelectedTimeslotIds.length;
      const entryWord = count > 1 ? "entries" : "entry";
      const word = count > 1 ? "unavailabilities" : "unavailability";

      window
        .sweetAlert({
          title: "Are you sure?",
          text: `Are you sure that you want to delete the ${count} selected ${entryWord}?`,
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            confirm: "Yes!",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.form.busy = true;

            this.$http
              .delete("/unavailabilities/batch", { body: { ids: this.altSelectedTimeslotIds } })
              .then((response) => {
                if (response.status === 200) {
                  window.toastr.success(
                    `The ${count} selected ${word} have been deleted!`,
                    "Success"
                  );
                }

                this.reset(true);
                this.$refs.availabilityWidget.refresh();
                this.$refs.unavailabilitiesList.init();
                this.form.busy = false;
                EventBus.$emit("user-updated");
              });
          }
        });
    },

    isPast(time) {
      return moment(time).isBefore(moment());
    },

    recurringTypeChange() {
      this.form.start_from_time = null;
      this.form.end_by_time = null;

      switch (this.form.recurring.type) {
        case "Monthly":
          this.rangePickerType = "month";
          break;

        default:
          this.rangePickerType = "date";
      }

      this.form.errors.clear();
    },

    isValidSubmission() {
      return true;
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.padder {
  border-top: 1px solid #e4e6eb;
  margin-top: 12px;
  padding-top: 20px;
}
</style>
